import dayjs from 'dayjs';
import type { EventReportPayload } from './eventReportApi';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 02 — Shared visual report. Rendered identically in the admin
 * `EventReportPage` and the public-share `PublicEventReportPage`.
 *
 * Print-friendly: hides chrome via `@media print` in the parent. Browsers
 * can save the page as PDF — the dedicated Puppeteer pipeline (Caso 02 fase
 * 4 of the PRD) is deferred per `docs-pend/CHECKLIST-INFRA.md`.
 */
export function EventReportView({
  report,
  variant,
}: {
  report: EventReportPayload;
  variant: 'internal' | 'public';
}) {
  const t = useT();
  const dates =
    report.event.startDate || report.event.endDate
      ? `${dayjs(report.event.startDate ?? undefined).format('MMM D, YYYY')} → ${dayjs(report.event.endDate ?? undefined).format('MMM D, YYYY')}`
      : '';

  return (
    <div className="space-y-6">
      <header className="border-b border-[var(--color-border-default)] pb-4">
        {report.branding?.logo && (
          <img
            src={report.branding.logo}
            alt={report.branding.campaignName ?? ''}
            className="mb-3 h-10 w-auto"
          />
        )}
        <h1 className="text-3xl font-bold">{report.event.name}</h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {report.event.address ?? ''} {dates && `· ${dates}`}
        </p>
        {report.branding?.campaignName && (
          <Badge tone="accent" className="mt-2">
            {report.branding.campaignName}
          </Badge>
        )}
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t('eventReport.reach.interactions')} value={report.reach.interactionsTotal} />
        <Kpi label={t('eventReport.reach.last24h')} value={report.reach.interactionsLast24h} />
        <Kpi label={t('eventReport.reach.distinctContacts')} value={report.reach.distinctContacts} />
        <Kpi label={t('eventReport.reach.leads')} value={report.reach.leadsCaptured} highlight />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('eventReport.prizes.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Row label={t('eventReport.prizes.allocated')} value={report.prizes.totalAllocated} />
            <Row label={t('eventReport.prizes.distributed')} value={report.prizes.totalDistributed} />
            <Row label={t('eventReport.prizes.remaining')} value={report.prizes.totalRemaining} />
            <Row label={t('eventReport.prizes.value')} value={`$${report.prizes.distributedValue.toFixed(2)}`} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('eventReport.costs.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Row
              label={t('eventReport.costs.perInteraction')}
              value={
                report.costs.costPerInteraction != null
                  ? `$${report.costs.costPerInteraction.toFixed(2)}`
                  : '—'
              }
            />
            <Row
              label={t('eventReport.costs.perLead')}
              value={
                report.costs.costPerLead != null
                  ? `$${report.costs.costPerLead.toFixed(2)}`
                  : '—'
              }
            />
          </CardContent>
        </Card>
      </section>

      {report.topAnswers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('eventReport.topAnswers.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {report.topAnswers.map((q) => (
              <div key={q.componentId}>
                <h4 className="text-sm font-semibold">{q.label}</h4>
                <ul className="mt-1 space-y-1">
                  {q.options.map((o) => (
                    <li
                      key={o.value}
                      className="flex items-center justify-between text-xs text-[var(--color-fg-muted)]"
                    >
                      <span>{o.value}</span>
                      <span className="tabular-nums">
                        {o.count} ({o.percent}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {Object.keys(report.demographics).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('eventReport.demographics.title')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(report.demographics).map(([key, buckets]) => (
              <div key={key}>
                <h4 className="text-xs font-semibold uppercase text-[var(--color-fg-muted)]">
                  {key}
                </h4>
                <ul className="mt-1 space-y-1">
                  {buckets.slice(0, 6).map((b) => (
                    <li
                      key={b.bucket}
                      className="flex items-center justify-between text-xs"
                    >
                      <span>{b.bucket}</span>
                      <span className="tabular-nums">{b.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {variant === 'public' && (
        <footer className="border-t border-[var(--color-border-default)] pt-3 text-xs text-[var(--color-fg-subtle)]">
          {t('eventReport.publicFooter')}
        </footer>
      )}
    </div>
  );
}

function Kpi(props: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={
        'rounded-xl border p-4 ' +
        (props.highlight
          ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft,rgba(178,98,245,0.08))]'
          : 'border-[var(--color-border-default)] bg-[var(--color-bg-surface)]')
      }
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
        {props.label}
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">
        {props.value.toLocaleString()}
      </div>
    </div>
  );
}

function Row(props: { label: string; value: number | string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[var(--color-fg-muted)]">{props.label}</span>
      <span className="font-semibold tabular-nums">
        {typeof props.value === 'number'
          ? props.value.toLocaleString()
          : props.value}
      </span>
    </div>
  );
}
